// Spec: .aidev/tasks/11083-preloader/docs/functional_requirements.md §Types
//
// The form library. Each builder returns one target position per bead plus two
// per-bead parameters that the form's own idle motion reads back, and each form
// declares whether it is a chart the report produces or an object that it does
// not. Nothing here knows about colour, the camera or the canvas.

export function gauss(rng) {
  return (rng() + rng() + rng() + rng() - 2) * 0.7
}
function alloc(n) {
  return { p: new Float32Array(n * 3), m: new Float32Array(n * 2) }
}

// ---- form builders -----------------------------------------------------
// Each returns { p:[x,y,z...], m:[m0,m1...] } of exactly n points, in model
// units: x +/-380, y +/-175 (y grows downward), z +/-180. m0 / m1 are
// form-specific parameters that the form's own idle motion reads back —
// "how far along the arm", "which segment", "which ring".

// Lay n droplets onto a fixed lattice, cycling when the lattice is smaller
// than the field. m0 carries normalised radius so a motion can ripple outward.
// Tip the solid toward the viewer. The camera only orbits horizontally, so an
// upright prism shows nothing but its rectangular silhouette however far the
// turntable turns -- the top face has to be brought into view by the model.
function tiltX(list, ang) {
  const c = Math.cos(ang),
    sn = Math.sin(ang)
  for (let i = 0; i < list.length; i++) {
    const y = list[i][1],
      z = list[i][2]
    list[i][1] = y * c - z * sn
    list[i][2] = y * sn + z * c
  }
  return list
}

function latticeFill(n, list, scale) {
  const r = alloc(n),
    L = list.length
  for (let q = 0; q < n; q++) {
    // A lattice larger than the field is sampled across its whole length, ends included: cycling by
    // remainder stops partway through, and a lattice is built axis by axis, so the tail it drops is a
    // whole corner of the solid.
    const pt = list[L <= n ? q % L : Math.round((q * (L - 1)) / (n - 1))]
    r.p[q * 3] = pt[0]
    r.p[q * 3 + 1] = pt[1]
    r.p[q * 3 + 2] = pt[2]
    const d = Math.sqrt(pt[0] * pt[0] + pt[1] * pt[1] + pt[2] * pt[2]) / (scale * 1.74)
    r.m[q * 2] = d > 1 ? 1 : d
    r.m[q * 2 + 1] = 0
  }
  return r
}

// ---- artwork forms -----------------------------------------------------
// Two forms are given as drawings rather than as formulae. Converting them in arithmetic rather
// than on a canvas keeps this library free of the DOM, so it stays testable.

// aytm's Skipper mark: a ship's helm.
const SKIPPER_PATH =
  'M462 117v14c-73 7-140 36-195 81l-10-11c-15-17-42-17-57 0-16 16-15 42 0 56l11 10c-45 54-73 122-81 196h-14c-22 0-42 16-42 38s19 40 42 39h14c7 73 36 140 81 196l-11 10c-17 15-17 40 0 56s42 15 57 0l10-11c54 45 122 73 195 81v14c-2 22 16 42 38 42s40-19 39-42v-14c73-7 140-36 195-81l10 11c15 17 40 17 56 0s15-42 0-56l-11-10c45-54 73-122 81-196h14c22 2 42-16 42-38s-19-40-42-39h-14c-7-73-36-140-81-196l11-10c17-15 17-40 0-56s-42-15-56 0l-10 11c-54-45-122-74-194-81v-14c2-22-16-42-38-42-23 0-41 20-40 42h0ZM321 729l90-99c18 13 39 21 62 26l-7 133c-54-6-104-27-144-60h0ZM371 590l-99 90c-32-40-53-90-61-146l134-6c4 23 13 44 26 62h0ZM212 465c6-54 28-104 61-146l99 90c-13 18-21 39-26 62l-133-6h0s0 0 0 0ZM411 369l-90-99c42-32 91-54 145-61l6 134c-23 5-44 13-62 26h0ZM591 369c-18-13-39-21-62-26l6-133c54 6 104 28 145 61l-90 99h0s0 0 0 0ZM681 729c-42 32-91 54-145 61l-6-135c22-4 44-13 62-26l90 100h0ZM731 681l-99-90c13-18 21-39 26-62l134 6c-6 54-28 104-61 146h0ZM731 319c32 42 54 91 61 146l-134 6c-4-22-13-44-26-62l99-90h0ZM422 500c0-44 36-80 80-80s80 36 80 80-36 80-80 80-80-36-80-80Z'

const SEAHORSE_PATH =
  'M29,63.7c-2.7-.4-5.4-2.2-5.3-5.1,0-1.8.9-3.2,1.5-4.6.5-1.3.4-2.2-.2-3.2-1-1.8-3.1-1.8-4.3-3.2-1.3-1.4-1.7-3.4-.7-5,2-3.5,5.6-3.6,9.1-2,4,1.8,6.3,2.9,9.4,7.1,2,2.8,6.6,8.6,4.8,13.5,0,.1-8.7,3.3-14.2,2.3h0ZM75.3,30.6c-2.5,2.3-4.3,3-7.7,1.7-1.9-.7-5-.4-6.9-.1-6.7.8-9.4.7-12.2-5.6-.7.4-1.2,1-1.9,1.4,1.6,2.5,2.1,4.7,4.3,7.1,1.4,1.5,5.2,2.3,7,3.4,7.6,4.7,10.5,12.1,7.3,19.9-1.9,4.5-7.3,8.5-9.7,10.5-7.5,6.2-9.2,22.9,1.2,20.1,1.6-.4,2.6-1.7,2.6-2.7,0-.8-.3-1.4-1.2-1.1-2.5.9-5.3.6-6.2-1.8-1.2-3.2,3.8-7.7,8.6-4.7,3.8,2.4,2.9,8.2.3,11.8-2.9,3.8-8,5-12.2,3.3-10.3-4.2-10.7-15.4-6.2-24.1,7.3-14-.7-24.1-12.1-32.5-.3-.2-.5-.8-.2-1.2,1.8-2.3,4.6-4.4,5.4-7.3.8-2.9-.9-5.3-1.8-7.9-.2-.5.2-1,.6-1.1,6.1-2,8.3-3.3,9.9-9.5.2-.7.9-.9,1.5-.5,4.8,3.4,8.2,2.5,12.1-1.8.5-.5,1.3-.4,1.6.3,2.8,9.6,7.8,15.7,15.9,21.3.5.3.6.9.2,1.3ZM57.7,21.5c0-1-.7-1.7-1.7-1.7-1,0-1.8.8-1.8,1.7,0,1,.7,1.7,1.6,1.6,1,0,1.8-.7,1.8-1.7h0Z'

const CURVE_STEPS = 14
const SILHOUETTE_ROWS = 240

// Path data to closed polylines. Only the commands these two drawings use are
// honoured; an arc would need its own conversion and neither drawing has one.
function flattenPath(d) {
  const tokens = d.match(/[a-z]|-?\d*\.?\d+(?:e[-+]?\d+)?/gi) || []
  const polys = []
  let poly = []
  let cmd = 'M'
  let x = 0
  let y = 0
  let sx = 0
  let sy = 0
  let px = 0
  let py = 0
  let i = 0

  const num = () => Number(tokens[i++])
  const push = (nx, ny) => {
    poly.push([nx, ny])
    x = nx
    y = ny
    px = nx // a smooth curve reflects the previous control point only after a curve
    py = ny
  }
  const cubic = (c1x, c1y, c2x, c2y, ex, ey) => {
    const ax = x
    const ay = y
    for (let s = 1; s <= CURVE_STEPS; s++) {
      const t = s / CURVE_STEPS
      const u = 1 - t
      poly.push([
        u * u * u * ax + 3 * u * u * t * c1x + 3 * u * t * t * c2x + t * t * t * ex,
        u * u * u * ay + 3 * u * u * t * c1y + 3 * u * t * t * c2y + t * t * t * ey
      ])
    }
    px = c2x
    py = c2y
    x = ex
    y = ey
  }
  const close = () => {
    if (poly.length > 2) polys.push(poly)
    poly = []
    x = sx
    y = sy
  }

  while (i < tokens.length) {
    const startedAt = i
    if (/[a-z]/i.test(tokens[i])) cmd = tokens[i++]
    if (i >= tokens.length && !/[zZ]/.test(cmd)) break
    const rel = cmd === cmd.toLowerCase()
    const ox = rel ? x : 0
    const oy = rel ? y : 0

    switch (cmd.toUpperCase()) {
      case 'M': {
        close()
        const mx = num() + ox
        const my = num() + oy
        sx = mx
        sy = my
        push(mx, my)
        cmd = rel ? 'l' : 'L'
        break
      }
      case 'L':
        push(num() + ox, num() + oy)
        break
      case 'H':
        push(num() + ox, y)
        break
      case 'V':
        push(x, num() + oy)
        break
      case 'C': {
        const c1x = num() + ox
        const c1y = num() + oy
        const c2x = num() + ox
        const c2y = num() + oy
        cubic(c1x, c1y, c2x, c2y, num() + ox, num() + oy)
        break
      }
      case 'S': {
        const c2x = num() + ox
        const c2y = num() + oy
        cubic(2 * x - px, 2 * y - py, c2x, c2y, num() + ox, num() + oy)
        break
      }
      case 'Z':
        close()
        break
      default:
        i++ // a command this drawing does not use; step past its argument
    }
    if (i === startedAt) i++ // nothing consumed: a stray number would otherwise spin here forever
  }
  close()
  return polys
}

// The runs of one horizontal line that fall inside the drawing, by the non-zero
// winding rule -- so the helm's spokes and the seahorse's eye stay open.
function rowSpans(polys, yy, out) {
  const hits = []
  for (const poly of polys) {
    for (let j = 0; j < poly.length; j++) {
      const a = poly[j]
      const b = poly[(j + 1) % poly.length]
      if (a[1] === b[1]) continue
      if (yy < Math.min(a[1], b[1]) || yy >= Math.max(a[1], b[1])) continue
      hits.push([a[0] + ((yy - a[1]) / (b[1] - a[1])) * (b[0] - a[0]), b[1] > a[1] ? 1 : -1])
    }
  }
  hits.sort((p, q) => p[0] - q[0])
  out.length = 0
  let wind = 0
  for (let j = 0; j < hits.length; j++) {
    const was = wind
    wind += hits[j][1]
    if (0 === was || 0 === wind) out.push(hits[j][0])
  }
  if (out.length % 2) out.pop()
  return out
}

// Lay n beads uniformly over the area a drawing covers, as a shallow slab so the
// shape still has a body when the turntable brings it side-on.
function silhouette(n, rng, d, opts) {
  const polys = flattenPath(d)
  let minX = 1e9
  let maxX = -1e9
  let minY = 1e9
  let maxY = -1e9
  for (const poly of polys)
    for (const point of poly) {
      if (point[0] < minX) minX = point[0]
      if (point[0] > maxX) maxX = point[0]
      if (point[1] < minY) minY = point[1]
      if (point[1] > maxY) maxY = point[1]
    }

  const rowH = (maxY - minY) / SILHOUETTE_ROWS
  const rows = []
  const cum = []
  const span = []
  let total = 0
  for (let j = 0; j < SILHOUETTE_ROWS; j++) {
    const yy = minY + (j + 0.5) * rowH
    rowSpans(polys, yy, span)
    let len = 0
    for (let k = 0; k + 1 < span.length; k += 2) len += span[k + 1] - span[k]
    rows.push({ y: yy, runs: span.slice(), len: len })
    total += len
    cum.push(total)
  }

  // A drawing this detailed cannot be filled by a few hundred beads, so each one chooses: `edge` of
  // them follow the contour, which is what the eye reads a shape by, and the rest fill the area.
  const edges = []
  let perim = 0
  for (const poly of polys)
    for (let j = 0; j < poly.length; j++) {
      const a = poly[j]
      const b = poly[(j + 1) % poly.length]
      const len = Math.hypot(b[0] - a[0], b[1] - a[1])
      if (len < 1e-6) continue
      perim += len
      edges.push({ a: a, b: b, at: perim })
    }

  const scale = Math.min(opts.width / (maxX - minX || 1), opts.height / (maxY - minY || 1))
  const cx = (minX + maxX) / 2
  const cy = (minY + maxY) / 2
  const onEdge = Math.round(n * (opts.edge || 0))
  const jitter = (opts.weight || 2) / scale
  const r = alloc(n)

  // Stratified, not purely random: n independent draws clump and leave holes, and
  // at this bead count the clumping is what the eye reads instead of the shape.
  for (let i = 0; i < onEdge; i++) {
    const want = ((i + rng()) / (onEdge || 1)) * perim
    let lo = 0
    let hi = edges.length - 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (edges[mid].at < want) lo = mid + 1
      else hi = mid
    }
    const e = edges[lo]
    const t = rng()
    const ex = e.a[0] + (e.b[0] - e.a[0]) * t + gauss(rng) * jitter
    const ey = e.a[1] + (e.b[1] - e.a[1]) * t + gauss(rng) * jitter
    r.p[i * 3] = (ex - cx) * scale
    r.p[i * 3 + 1] = (ey - cy) * scale
    r.p[i * 3 + 2] = (rng() - 0.5) * opts.depth
    r.m[i * 2] = (ey - minY) / (maxY - minY || 1)
    r.m[i * 2 + 1] = (ex - cx) * scale
  }

  for (let i = onEdge; i < n; i++) {
    // Rows are drawn in proportion to how much of them the drawing covers, so a
    // narrow tail comes out no denser than a broad body.
    const want = ((i - onEdge + rng()) / (n - onEdge || 1)) * total
    let lo = 0
    let hi = SILHOUETTE_ROWS - 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (cum[mid] < want) lo = mid + 1
      else hi = mid
    }
    const row = rows[lo]
    let along = rng() * (row.len || 1)
    let ax = row.runs.length ? row.runs[0] : cx
    for (let k = 0; k + 1 < row.runs.length; k += 2) {
      const w = row.runs[k + 1] - row.runs[k]
      if (along <= w) {
        ax = row.runs[k] + along
        break
      }
      along -= w
    }
    const ay = row.y + (rng() - 0.5) * rowH
    r.p[i * 3] = (ax - cx) * scale
    r.p[i * 3 + 1] = (ay - cy) * scale
    r.p[i * 3 + 2] = (rng() - 0.5) * opts.depth
    r.m[i * 2] = (ay - minY) / (maxY - minY || 1)
    r.m[i * 2 + 1] = (ax - cx) * scale
  }
  return r
}

export const BUILDERS = {
  // ---------- ORDER ----------

  bars: function (n, rng) {
    let k = 7,
      hs = [],
      i
    for (i = 0; i < k; i++) hs.push(70 + rng() * 230)
    const total = hs.reduce(function (a, b) {
      return a + b
    }, 0)
    let r = alloc(n),
      bar = 0,
      acc = 0
    for (i = 0; i < n; i++) {
      const want = (i / n) * total
      while (bar < k - 1 && want > acc + hs[bar]) {
        acc += hs[bar]
        bar++
      }
      const u = rng()
      r.p[i * 3] = -300 + bar * 100 + (rng() - 0.5) * 56
      r.p[i * 3 + 1] = 160 - u * hs[bar]
      r.p[i * 3 + 2] = (rng() - 0.5) * 56
      r.m[i * 2] = u
      r.m[i * 2 + 1] = bar
    }
    return r
  },

  // A flat annulus with a real hole and arcs of differing span, rather than a
  // torus: a round tube reads as a ring doughnut, not as a chart.
  donut: function (n, rng) {
    let RO = 152,
      RI = 84,
      segs = 5,
      gap = 0.085,
      i
    let span = [],
      tot = 0
    for (i = 0; i < segs; i++) {
      span.push(0.5 + rng())
      tot += span[i]
    }
    const usable = 6.283185 - segs * gap
    let start = [],
      acc = -1.5708
    for (i = 0; i < segs; i++) {
      span[i] = (span[i] / tot) * usable
      start.push(acc + gap / 2)
      acc += span[i] + gap
    }
    const r = alloc(n)
    for (i = 0; i < n; i++) {
      let seg = 0,
        pick = rng() * usable,
        run = 0
      for (let j = 0; j < segs; j++) {
        run += span[j]
        if (pick <= run) {
          seg = j
          break
        }
        seg = j
      }
      const th = start[seg] + rng() * span[seg]
      const rr = Math.sqrt(RI * RI + rng() * (RO * RO - RI * RI)) // uniform over area
      r.p[i * 3] = rr * Math.cos(th)
      r.p[i * 3 + 1] = rr * Math.sin(th) * 0.94
      r.p[i * 3 + 2] = (rng() - 0.5) * 40 // a thin disc, not a tube
      r.m[i * 2] = th
      r.m[i * 2 + 1] = seg
    }
    return r
  },

  // A competitive landscape: several brands as peaks over a shared plane, with
  // elevation banded onto the segment colours so height reads as colour rather
  // than relying on the depth ramp, which is already spoken for.
  topography: function (n, rng) {
    let peaks = [],
      np = 4 + Math.floor(rng() * 2),
      i
    for (i = 0; i < np; i++) {
      peaks.push({ x: (rng() - 0.5) * 520, z: (rng() - 0.5) * 230, a: 96 + rng() * 150, s: 62 + rng() * 74 })
    }
    function height(x, z) {
      let h = 0
      for (let j = 0; j < peaks.length; j++) {
        const pk = peaks[j],
          dx = (x - pk.x) / pk.s,
          dz = (z - pk.z) / pk.s
        h += pk.a * Math.exp(-(dx * dx + dz * dz))
      }
      return h
    }
    const r = alloc(n)
    let xs = new Float32Array(n),
      zs = new Float32Array(n),
      hs = new Float32Array(n),
      hmax = 1
    for (i = 0; i < n; i++) {
      xs[i] = (rng() - 0.5) * 690
      zs[i] = (rng() - 0.5) * 310
      hs[i] = height(xs[i], zs[i])
      if (hs[i] > hmax) hmax = hs[i]
    }
    for (i = 0; i < n; i++) {
      const u = hs[i] / hmax
      r.p[i * 3] = xs[i]
      // Seen from above at roughly 60 degrees: depth carries most of the
      // screen height and relief only a little, so elevation reads as colour
      // and parallax rather than as a skyline.
      r.p[i * 3 + 1] = 20 - hs[i] * 0.62 + (rng() - 0.5) * 5
      r.p[i * 3 + 2] = zs[i]
      r.m[i * 2] = u
      r.m[i * 2 + 1] = 4 - Math.min(4, Math.floor(u * 5)) // high ground takes the warm end
    }
    return r
  },

  stacked: function (n, rng) {
    let k = 5,
      cols = [],
      i,
      j
    for (i = 0; i < k; i++) {
      const segs = 3 + Math.floor(rng() * 2),
        parts = []
      for (j = 0; j < segs; j++) parts.push(0.4 + rng())
      const s = parts.reduce(function (a, b) {
        return a + b
      }, 0)
      cols.push(
        parts.map(function (v) {
          return v / s
        })
      )
    }
    const r = alloc(n)
    for (i = 0; i < n; i++) {
      const col = Math.floor(rng() * k),
        parts2 = cols[col]
      let seg = Math.floor(rng() * parts2.length),
        base = 0
      for (j = 0; j < seg; j++) base += parts2[j]
      const frac = base + rng() * parts2[seg] * 0.82
      r.p[i * 3] = -240 + col * 120 + (rng() - 0.5) * 60
      r.p[i * 3 + 1] = 160 - frac * 290
      r.p[i * 3 + 2] = (rng() - 0.5) * 60
      r.m[i * 2] = frac
      r.m[i * 2 + 1] = seg
    }
    return r
  },

  // An asymmetric swell: long shoulder, steep leading face, curling lip.
  wave: function (n, rng) {
    const A = 132,
      xc = 0.24
    const r = alloc(n)
    for (let i = 0; i < n; i++) {
      const t = rng() * 2 - 1
      const top = 148 - A * profileAsym(t, xc) - 26
      const surf = rng() < 0.58
      r.p[i * 3] = t * 358
      r.p[i * 3 + 1] = surf ? top + gauss(rng) * 8 : top + rng() * (166 - top)
      r.p[i * 3 + 2] = (rng() - 0.5) * 150
      r.m[i * 2] = t
      r.m[i * 2 + 1] = surf ? 1 : 0
    }
    return r
  },

  // The one manufactured object in the set: shackle ring, shank, stock and
  // two barbed flukes. Built as solid parts so it reads as an object on the
  // turntable rather than an outline that vanishes edge-on.
  anchor: function (n, rng) {
    let r = alloc(n),
      k = 0,
      i
    function put(x, y, z, m0, m1) {
      if (k >= n) return
      r.p[k * 3] = x
      r.p[k * 3 + 1] = y
      r.p[k * 3 + 2] = z
      r.m[k * 2] = m0
      r.m[k * 2 + 1] = m1
      k++
    }
    const qRing = Math.round(n * 0.13),
      qShank = Math.round(n * 0.24),
      qStock = Math.round(n * 0.17)
    for (i = 0; i < qRing; i++) {
      const th = rng() * 6.283185,
        tr = 14 * Math.sqrt(rng()),
        ph = rng() * 6.283185
      const ring = 34 + tr * Math.cos(ph)
      put(ring * Math.cos(th), -142 + ring * Math.sin(th), tr * Math.sin(ph), 0, 0)
    }
    for (i = 0; i < qShank; i++) {
      const u = rng()
      put((rng() - 0.5) * 30, -110 + u * 202, (rng() - 0.5) * 30, u, 1)
    }
    for (i = 0; i < qStock; i++) {
      const v = rng() * 2 - 1
      put(v * 106, -80 + (rng() - 0.5) * 22, (rng() - 0.5) * 22, Math.abs(v), 2)
    }
    const rest = n - k
    for (i = 0; i < rest; i++) {
      const side = i % 2 ? 1 : -1
      const t = Math.pow(rng(), 0.9)
      const thick = t > 0.8 ? 8 + ((t - 0.8) / 0.2) * 27 : 15 - t * 7
      put(
        side * 154 * Math.sin(t * 1.25) + gauss(rng) * thick,
        92 - 154 * (1 - Math.cos(t * 1.25)) + gauss(rng) * thick * 0.8,
        (rng() - 0.5) * 30,
        t,
        3
      )
    }
    while (k < n) put(0, 0, 0, 0, 1)
    return r
  },

  // ---------- LATTICE SOLIDS ----------
  // Points sit on exact lattice positions with no jitter at all -- the whole
  // point of these three is that they are perfect where everything else is
  // organic. Each picks its own subdivision so the lattice lands as close to
  // the requested droplet count as possible, then cycles to fill exactly.

  cube: function (n) {
    let E = 152,
      N = 3,
      bd = 1e9,
      i,
      j,
      k
    for (i = 3; i <= 34; i++) {
      const d = Math.abs(6 * i * i - 12 * i + 8 - n)
      if (d < bd) {
        bd = d
        N = i
      }
    }
    const list = []
    for (i = 0; i < N; i++)
      for (j = 0; j < N; j++)
        for (k = 0; k < N; k++) {
          if (i && j && k && i < N - 1 && j < N - 1 && k < N - 1) continue // hollow shell
          list.push([(i / (N - 1) - 0.5) * 2 * E, (j / (N - 1) - 0.5) * 2 * E, (k / (N - 1) - 0.5) * 2 * E])
        }
    return latticeFill(n, tiltX(list, -0.3), E)
  },

  octahedron: function (n) {
    let E = 168,
      M = 3,
      bd = 1e9,
      i,
      j
    // The largest subdivision that still fits the field: a lattice with more positions than there
    // are beads cannot place them all, and the ones it drops are corners.
    for (i = 3; i <= 40; i++) {
      const size = 4 * i * i + 2
      const d = Math.abs(size - n)
      if (size <= n && d < bd) {
        bd = d
        M = i
      }
    }
    const list = []
    for (i = -M; i <= M; i++)
      for (j = -M; j <= M; j++) {
        const rem = M - Math.abs(i) - Math.abs(j)
        if (rem < 0) continue
        const x = (i / M) * E,
          z = (j / M) * E
        if (rem === 0) list.push([x, 0, z])
        else {
          list.push([x, (rem / M) * E * 1.3, z])
          list.push([x, -(rem / M) * E * 1.3, z])
        }
      }
    return latticeFill(n, list, E)
  },

  hexprism: function (n) {
    let R = 148,
      H = 150,
      RE = 4,
      HH = 6,
      bd = 1e9,
      a,
      b,
      i
    for (a = 3; a <= 22; a++)
      for (b = 3; b <= 26; b++) {
        const d = Math.abs(6 * a * b + 12 * a - n)
        if (d < bd) {
          bd = d
          RE = a
          HH = b
        }
      }
    const list = [],
      TAU = 6.283185
    function corner(c) {
      return [Math.cos((c * TAU) / 6) * R, Math.sin((c * TAU) / 6) * R]
    }
    for (i = 0; i < 6; i++) {
      // six side faces as grids
      const p0 = corner(i),
        p1 = corner((i + 1) % 6)
      for (a = 0; a < RE; a++)
        for (b = 0; b < HH; b++) {
          const t = a / (RE - 1 || 1)
          list.push([p0[0] + (p1[0] - p0[0]) * t, (b / (HH - 1 || 1) - 0.5) * 2 * H, p0[1] + (p1[1] - p0[1]) * t])
        }
    }
    for (i = 0; i < 6; i++) {
      // and the two cap outlines
      const q0 = corner(i),
        q1 = corner((i + 1) % 6)
      for (a = 0; a < RE; a++) {
        const u = a / (RE - 1 || 1)
        const cx = q0[0] + (q1[0] - q0[0]) * u,
          cz = q0[1] + (q1[1] - q0[1]) * u
        list.push([cx, -H, cz])
        list.push([cx, H, cz])
      }
    }
    return latticeFill(n, tiltX(list, -0.46), R)
  },

  // ---------- CHAOS ----------

  swell: function (n, rng) {
    const r = alloc(n)
    for (let i = 0; i < n; i++) {
      const x = (rng() - 0.5) * 740,
        z = (rng() - 0.5) * 340
      const ph = x * 0.011 + z * 0.006
      r.p[i * 3] = x
      r.p[i * 3 + 1] = Math.sin(ph) * 72 + (rng() - 0.5) * 14
      r.p[i * 3 + 2] = z
      r.m[i * 2] = ph
      r.m[i * 2 + 1] = 0
    }
    return r
  },

  // A funnel surface, with two spiral arms picked out by density rather than
  // by tying the bearing to the radius (which collapses the set onto one bearing).
  vortex: function (n, rng) {
    let r = alloc(n),
      got = 0,
      guard = 0
    while (got < n && guard < n * 60) {
      guard++
      const u = Math.pow(rng(), 0.5)
      const th = rng() * 6.283185
      const streak = 0.5 + 0.5 * Math.cos(2 * (th + u * 5))
      if (rng() > 0.3 + 0.7 * streak * streak) continue
      const rad = 30 + u * 300,
        z = rad * Math.sin(th)
      r.p[got * 3] = rad * Math.cos(th)
      r.p[got * 3 + 1] = -74 + Math.pow(1 - u, 1.6) * 214
      r.p[got * 3 + 2] = z
      r.m[got * 2] = u
      r.m[got * 2 + 1] = th
      got++
    }
    return r
  },

  // Fusiform body pinched to a narrow wrist. The main silhouette is sampled
  // evenly so body and fins share one density, but the caudal fin is built
  // separately as radiating rays -- an area-proportional scatter always leaves
  // a tail threadbare, and a solid lobe reads as a paddle rather than plumage.
  fish: function (n, rng) {
    let S = 210,
      r = alloc(n),
      k = 0
    function body(x) {
      if (x < -0.42 || x > 0.98) return 0
      const q = 1 - Math.pow((x - 0.02) / 0.94, 2)
      const nose = x > 0.68 ? Math.pow((1 - x) / 0.32, 0.72) : 1 // taper to a point
      return q > 0 ? 0.335 * Math.pow(q, 0.6) * nose : 0
    }
    function inMain(x, y) {
      const ay = Math.abs(y)
      if (y < 0 && x > -0.28 && x < 0.34) {
        // dorsal
        const d = 0.36 * Math.pow(Math.max(0, 1 - Math.abs((x + 0.02) / 0.34)), 0.62)
        if (d > 0 && ay < body(x) + d) return true
      }
      if (y > 0 && x > -0.48 && x < 0.02) {
        // anal
        const af = 0.2 * Math.max(0, 1 - Math.abs((x + 0.23) / 0.25))
        if (af > 0 && ay < body(x) + af) return true
      }
      const qx = (x - 0.3) / 0.24,
        qy = (y - 0.125) / 0.07 // pectoral
      if (qx * qx + qy * qy < 1) return true
      const vx = (x - 0.02) / 0.13,
        vy = (y - 0.175) / 0.055 // pelvic
      if (vx * vx + vy * vy < 1) return true
      if (x > 0.98 || x < -0.6) return false
      if (x >= -0.42) {
        if (ay > body(x)) return false
      } else {
        const t = (-x - 0.42) / 0.18 // wrist
        if (ay > body(-0.42) * (1 - t) + 0.045 * t) return false
      }
      const ex = x - 0.66,
        ey = y + 0.09
      return !(ex * ex + ey * ey < 0.004) // eye
    }
    function put(x, y, z) {
      if (k >= n) return
      r.p[k * 3] = x * S
      r.p[k * 3 + 1] = y * S
      r.p[k * 3 + 2] = z
      r.m[k * 2] = x
      r.m[k * 2 + 1] = 0
      k++
    }
    let mainN = Math.round(n * 0.78),
      got = 0,
      guard = 0
    while (got < mainN && guard < mainN * 600) {
      guard++
      const x = -0.62 + rng() * 1.62,
        y = -0.66 + rng() * 1.16
      if (!inMain(x, y)) continue
      put(x, y, (rng() - 0.5) * 46)
      got++
    }
    const rays = 9,
      tailN = n - k
    for (let i = 0; i < tailN; i++) {
      const j = i % rays
      const f = rays === 1 ? 0 : (j / (rays - 1)) * 2 - 1
      const ang = Math.PI + f * 0.8
      const len = 0.24 + 0.2 * Math.abs(f) // outer rays longest -> forked
      const u = Math.pow(rng(), 0.82)
      const th = (1 - u) * 0.022 + 0.01
      put(
        -0.6 + Math.cos(ang) * u * len + gauss(rng) * th,
        Math.sin(ang) * u * len + gauss(rng) * th,
        (rng() - 0.5) * 18
      )
    }
    while (k < n) put(0, 0, 0)
    return r
  },

  bubbles: function (n, rng) {
    let k = 6,
      xs = [],
      i
    for (i = 0; i < k; i++) xs.push(-300 + i * 120 + (rng() - 0.5) * 40)
    const r = alloc(n)
    for (i = 0; i < n; i++) {
      const col = Math.floor(rng() * k),
        u = rng()
      r.p[i * 3] = xs[col] + Math.sin(u * 7 + col) * 26 + (rng() - 0.5) * 16
      r.p[i * 3 + 1] = 168 - u * 330
      r.p[i * 3 + 2] = (rng() - 0.5) * 200
      r.m[i * 2] = u
      r.m[i * 2 + 1] = col
    }
    return r
  },

  ripples: function (n, rng) {
    const r = alloc(n)
    for (let i = 0; i < n; i++) {
      const ring = 1 + Math.floor(rng() * 5)
      const rad = ring * 62 + gauss(rng) * 9
      const th = rng() * 6.283185
      const z = rad * Math.sin(th) * 0.92
      r.p[i * 3] = rad * Math.cos(th)
      r.p[i * 3 + 1] = 30 + (Math.cos(ring * 1.6) * 24) / ring
      r.p[i * 3 + 2] = z
      r.m[i * 2] = ring
      r.m[i * 2 + 1] = th
    }
    return r
  },

  // Anatomy in the round: a mantle sampled as a solid dome, and eight arms
  // whose bases are spaced around the vertical axis rather than fanned across
  // a plane — so the silhouette holds up from any bearing on the turntable.
  octopus: function (n, rng) {
    const r = alloc(n)
    const hy = -84,
      hrx = 78,
      hry = 92,
      hrz = 78
    let headN = Math.floor(n * 0.38),
      got = 0,
      guard = 0
    while (got < headN && guard < headN * 60) {
      guard++
      const ux = rng() * 2 - 1,
        uy = rng() * 2 - 1,
        uz = rng() * 2 - 1
      if (ux * ux + uy * uy + uz * uz > 1) continue
      const px = ux * hrx
      const py = hy + uy * hry * (uy < 0 ? 1 : 0.8)
      const pz = uz * hrz
      if (pz > 6) {
        // eyes, carved into the front
        const ey = py - (hy + 26)
        if ((px - 40) * (px - 40) + ey * ey * 2 < 360) continue
        if ((px + 40) * (px + 40) + ey * ey * 2 < 360) continue
      }
      r.p[got * 3] = px
      r.p[got * 3 + 1] = py
      r.p[got * 3 + 2] = pz
      r.m[got * 2] = 0
      r.m[got * 2 + 1] = -1
      got++
    }
    const armN = n - got,
      arms = 8,
      base = got
    for (let i = 0; i < armN; i++) {
      const a = i % arms
      const phi = (a * 6.283185) / arms + 0.2
      const u = Math.pow(rng(), 0.85)
      const len = 196 + ((a * 41) % 54)
      const rh = len * 0.66 * Math.sin(u * 2.05) // out, then curling back in
      const thick = (1 - u) * 11 + 2.5
      const k = base + i
      r.p[k * 3] = Math.cos(phi) * rh + gauss(rng) * thick
      r.p[k * 3 + 1] = hy + 64 + u * len * 0.8 + gauss(rng) * thick
      r.p[k * 3 + 2] = Math.sin(phi) * rh + gauss(rng) * thick
      r.m[k * 2] = u
      r.m[k * 2 + 1] = a
    }
    return r
  },

  // ---------- ARTWORK ----------

  skipper: function (n, rng) {
    return silhouette(n, rng, SKIPPER_PATH, { width: 340, height: 300, depth: 20, edge: 0 })
  },

  seahorse: function (n, rng) {
    return silhouette(n, rng, SEAHORSE_PATH, { width: 300, height: 304, depth: 16, edge: 0.72, weight: 0.38 })
  }
}

function profileAsym(t, xc) {
  const w = t < xc ? 0.62 : 0.19
  const d = (t - xc) / w
  return Math.exp(-d * d)
}

// ---- per-form idle motion ---------------------------------------------
// Each form animates the thing it *is*: ripples travel outward, the fish
// undulates tail-to-nose, the vortex rotates faster at its throat. Written
// as a displacement so the same routine works during a hold and can be
// cross-faded through a transition.

/* eslint-disable max-params -- one dispatch signature, called per bead per frame: the engine
   looks an entry up by form key and calls it generically, so every entry takes the same seven
   arguments. Bundling them would allocate an object per bead per frame in the render loop. */
export const MOTION = {
  cube: function (o, x, y, z, m0, m1, T) {
    // a ripple runs through the lattice
    const w = Math.sin(T * 1.3 - m0 * 5.5) * 6
    o[0] = x * 0.012 * w * 0.2
    o[1] = w
    o[2] = z * 0.012 * w * 0.2
  },
  octahedron: function (o, x, y, z, m0, m1, T) {
    // turns against the turntable
    const a = -T * 0.5,
      c = Math.cos(a),
      sn = Math.sin(a)
    o[0] = x * c - z * sn - x
    o[1] = 0
    o[2] = x * sn + z * c - z
  },
  hexprism: function (o, x, y, z, m0, m1, T) {
    // spins on its own axis, and breathes
    const a = T * 0.62,
      c = Math.cos(a),
      sn = Math.sin(a)
    o[0] = x * c - z * sn - x
    o[1] = Math.sin(T * 0.9) * 5
    o[2] = x * sn + z * c - z
  },

  bars: function (o, x, y, z, m0, m1, T) {
    o[0] = 0
    o[1] = -m0 * Math.sin(T * 1.15 + m1 * 1.25) * 13
    o[2] = 0
  },
  donut: function (o, x, y, z, m0, m1, T) {
    // spins in its own plane
    const a = T * 0.34,
      c = Math.cos(a),
      s = Math.sin(a)
    o[0] = x * c - y * s - x
    o[1] = x * s + y * c - y
    o[2] = 0
  },
  topography: function (o, x, y, z, m0, m1, T) {
    // peaks rise and settle
    o[0] = 0
    o[1] = -Math.sin(T * 0.85 + m0 * 4.2) * 9 * m0
    o[2] = 0
  },
  stacked: function (o, x, y, z, m0, m1, T) {
    o[0] = 0
    o[1] = Math.sin(T * 1.25 + m1 * 1.6) * 7
    o[2] = 0
  },
  wave: function (o, x, y, z, m0, m1, T) {
    // the crest surges and leans
    const p = profileAsym(m0, 0.24),
      s = Math.sin(T * 0.9)
    o[0] = s * 20 * p
    o[1] = -s * 15 * p
    o[2] = 0
  },
  anchor: function (o, x, y, z, m0, m1, T) {
    // swings from its shackle
    const a = Math.sin(T * 0.85) * 0.08,
      px = x,
      py = y + 142
    o[0] = px * Math.cos(a) - py * Math.sin(a) - px
    o[1] = px * Math.sin(a) + py * Math.cos(a) - py + Math.sin(T * 0.6) * 5
    o[2] = 0
  },
  swell: function (o, x, y, z, m0, m1, T) {
    // the wave travels
    o[0] = 0
    o[1] = (Math.sin(m0 - T * 1.5) - Math.sin(m0)) * 72
    o[2] = 0
  },
  vortex: function (o, x, y, z, m0, m1, T) {
    // faster at the throat
    const a = T * (0.22 + (1 - m0) * 1.6),
      c = Math.cos(a),
      s = Math.sin(a)
    o[0] = x * c - z * s - x
    o[1] = 0
    o[2] = x * s + z * c - z
  },
  fish: function (o, x, y, z, m0, m1, T) {
    // undulates, tail leading
    const amp = 26 * Math.pow(1 - (m0 + 1) / 2, 1.3)
    const w = Math.sin(m0 * 3.4 - T * 4.2)
    o[0] = 0
    o[1] = w * amp * 0.28
    o[2] = w * amp
  },
  bubbles: function (o, x, y, z, m0, m1, T) {
    // wobble on the way up
    o[0] = Math.sin(T * 1.9 + m0 * 7 + m1) * 10
    o[1] = -Math.sin(T * 0.85 + m0 * 5) * 13
    o[2] = Math.cos(T * 1.6 + m0 * 6 + m1) * 8
  },
  ripples: function (o, x, y, z, m0, m1, T) {
    // rings pulse outward in turn
    const dr = Math.sin(T * 1.7 - m0 * 1.15) * 16
    const dz = Math.sin(m1) * 0.92 * dr
    o[0] = Math.cos(m1) * dr
    o[1] = dz * 0.42
    o[2] = dz
  },
  octopus: function (o, x, y, z, m0, m1, T) {
    if (m1 < 0) {
      // mantle breathes
      const k = Math.sin(T * 1.2) * 0.05
      o[0] = x * k
      o[1] = (y + 84) * k
      o[2] = z * k
      return
    }
    const phi = (m1 * 6.283185) / 8 + 0.2
    const s = Math.sin(m0 * 4.5 - T * 2.6 + m1 * 0.8) * 17 * m0
    o[0] = Math.cos(phi + 1.5708) * s
    o[1] = Math.cos(m0 * 4.5 - T * 2.6 + m1 * 0.8) * 11 * m0
    o[2] = Math.sin(phi + 1.5708) * s
  },
  skipper: function (o, x, y, z, m0, m1, T) {
    // a helm turns: the wheel rolls slowly about its own hub, easing either way
    const a = Math.sin(T * 0.45) * 0.42
    const c = Math.cos(a)
    const sn = Math.sin(a)
    o[0] = x * c - y * sn - x
    o[1] = x * sn + y * c - y
    o[2] = 0
  },
  seahorse: function (o, x, y, z, m0, m1, T) {
    // holds station and curls: the sway grows toward the tail, which is m0 = 1
    const t = m0 * m0
    o[0] = Math.sin(T * 1.15 - m0 * 3.4) * (1.5 + t * 8)
    o[1] = Math.sin(T * 0.7) * 5
    o[2] = Math.cos(T * 1.15 - m0 * 3.4) * (1 + t * 3)
  }
}
/* eslint-enable max-params */

export const FORMS = [
  { key: 'bars', label: 'Bar columns', kind: 'order', tint: 0, home: (-20 * Math.PI) / 180 },
  { key: 'swell', label: 'Swell', kind: 'chaos', tint: 0, home: (30 * Math.PI) / 180 },
  { key: 'donut', label: 'Donut ring', kind: 'order', tint: 0.85, home: (8 * Math.PI) / 180 },
  { key: 'vortex', label: 'Whirlpool', kind: 'chaos', tint: 0, home: (34 * Math.PI) / 180 },
  { key: 'cube', label: 'Cube lattice', kind: 'order', tint: 0, home: (25 * Math.PI) / 180 },
  { key: 'anchor', label: 'Anchor', kind: 'chaos', tint: 0, home: (12 * Math.PI) / 180 },
  { key: 'topography', label: 'Topography', kind: 'order', tint: 0.92, home: (-11 * Math.PI) / 180 },
  { key: 'fish', label: 'Fish', kind: 'chaos', tint: 0, home: (-22 * Math.PI) / 180 },
  { key: 'octahedron', label: 'Octahedron', kind: 'order', tint: 0, home: (18 * Math.PI) / 180 },
  { key: 'bubbles', label: 'Bubbles', kind: 'chaos', tint: 0, home: (6 * Math.PI) / 180 },
  { key: 'wave', label: 'Breaking wave', kind: 'order', tint: 0, home: (38 * Math.PI) / 180 },
  { key: 'ripples', label: 'Ripples', kind: 'chaos', tint: 0, home: 0 },
  { key: 'hexprism', label: 'Hex prism', kind: 'order', tint: 0, home: (26 * Math.PI) / 180 },
  { key: 'octopus', label: 'Octopus', kind: 'chaos', tint: 0, home: (18 * Math.PI) / 180 },
  // The mark is a helm: a made object, and it keeps the order/chaos alternation whole.
  { key: 'skipper', label: 'Skipper helm', kind: 'order', tint: 0, home: (10 * Math.PI) / 180, bead: 1.7 },
  { key: 'seahorse', label: 'Seahorse', kind: 'chaos', tint: 0, home: (-14 * Math.PI) / 180 }
]
